/**
 * Step 2 of the Add User wizard: primary company / group, additional sites,
 * and the (mandatory) user role.
 */
import type { Company, Role } from '../../lib/api';
import { Label } from '../../components/ui/label';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Checkbox } from '../../components/ui/checkbox';
import { Badge } from '../../components/ui/badge';
import { Layers, type LucideIcon } from 'lucide-react';
import type { InviteCompaniesState } from './useInviteCompanies';

interface CompaniesStepProps {
  sites: InviteCompaniesState;
  /** Truck for supplier invites, Building2 for customer invites. */
  TypeIcon: LucideIcon;
  roles: Role[];
  roleId: number | null;
  onRoleChange: (roleId: number) => void;
}

export function CompaniesStep({ sites, TypeIcon, roles, roleId, onRoleChange }: CompaniesStepProps) {
  const {
    companies, companyGroups, groupsInScope, primarySelection, selectPrimary,
    primaryCompanyId, primaryCompany, additionalSiteIds, setAdditionalSiteIds,
    sitesUnderGroup, divisionsUnderGroup, sisterSites, selectedSiteIds,
  } = sites;

  return (
    <Card>
      <CardHeader>
        <CardTitle>2. Related companies</CardTitle>
        <CardDescription>
          Pick the primary company, then any additional sites in the same group the user should have access to.
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-5">
        <div className="space-y-2">
          <Label>Primary company *</Label>
          <Select value={primarySelection} onValueChange={selectPrimary}>
            <SelectTrigger><SelectValue placeholder="Select company, division, or group…" /></SelectTrigger>
            <SelectContent>
              {/* CR-5313 round 2: top-level Groups come first, then
                  Divisions (groups with a parent), then individual
                  Sites. Each row tags its hierarchy level so the
                  admin can see what they're picking.
                  CR-5313 round 3 (issue 1): filtered to ``groupsInScope``
                  so a customer invite only surfaces customer groups
                  and a supplier invite only surfaces supplier groups —
                  mixing the two was Sarah's first 2026-05-20 rework
                  flag (Ironvale / Skybridge etc surfaced under the
                  customer dropdown). */}
              {groupsInScope.filter(g => g.parent_group == null).length > 0 && (
                <div className="px-2 pt-1 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">Groups</div>
              )}
              {groupsInScope.filter(g => g.parent_group == null).map(g => (
                <SelectItem key={`group-${g.id}`} value={`group:${g.id}`}>
                  <div className="flex items-center gap-2">
                    <Layers className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">{g.name}</span>
                    <Badge variant="outline" className="text-[10px]">Group</Badge>
                  </div>
                </SelectItem>
              ))}
              {groupsInScope.filter(g => g.parent_group != null).length > 0 && (
                <div className="px-2 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">Divisions</div>
              )}
              {groupsInScope.filter(g => g.parent_group != null).map(g => (
                <SelectItem key={`group-${g.id}`} value={`group:${g.id}`}>
                  <div className="flex items-center gap-2 pl-3">
                    <Layers className="h-4 w-4 text-muted-foreground" />
                    <span>{g.name}</span>
                    <Badge variant="outline" className="text-[10px]">Division</Badge>
                  </div>
                </SelectItem>
              ))}
              <div className="px-2 pt-2 pb-0.5 text-[10px] uppercase tracking-wide text-muted-foreground">Sites</div>
              {companies.map(c => (
                <SelectItem key={`site-${c.id}`} value={`site:${c.id}`}>
                  <div className="flex items-center gap-2 pl-6">
                    <TypeIcon className="h-4 w-4 text-muted-foreground" />
                    {c.name}
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <p className="text-xs text-muted-foreground">
            Picking a Group or Division grants access to every site beneath it. Picking a Site anchors the user there and lets you tick additional sister sites below.
          </p>
          {primarySelection.startsWith('group:') && primaryCompanyId > 0 && (
            <p className="text-xs text-blue-700 dark:text-blue-300">
              User will be granted access to {1 + additionalSiteIds.size} site{additionalSiteIds.size === 0 ? '' : 's'} under the selected {primarySelection.includes(':') && companyGroups.find(g => g.id === parseInt(primarySelection.split(':')[1]))?.parent_group != null ? 'division' : 'group'}.
            </p>
          )}
        </div>

        {primaryCompanyId > 0 && (
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Layers className="h-3.5 w-3.5" />{!primarySelection.startsWith('group:') && !primaryCompany?.group ? 'Additional sites' : <>Sites in this {primarySelection.startsWith('group:') && companyGroups.find(g => g.id === parseInt(primarySelection.split(':')[1]))?.parent_group != null ? 'division' : 'group'}</>}
            </Label>
            {/* CR-5313 round 3 (issues 2 + 3): when a top-level Group
                is anchored, render its divisions as sub-headers with
                their sites grouped under each — Sarah's expected
                "all divisions and all sites within those divisions"
                layout. When a Division is anchored, list every site
                beneath the division including the primary (Sarah's
                Toulouse-missing flag was the primary being excluded
                from the picker because it had been auto-selected as
                anchor). When a Site is anchored, fall back to the
                sister-site picker. The primary site always shows as
                locked-checked since unticking it would invalidate
                the anchor.

                Coverage matrix per Sarah's 2026-05-20 issues:
                - Group anchor → divisions listed + sites grouped under each ✓
                - Division anchor → both Toulouse and Daresbury visible ✓
                - Site anchor → sister sites in same group (legacy) ✓
            */}
            {(() => {
              const isGroupAnchor = primarySelection.startsWith('group:');
              const anchorGroupId = isGroupAnchor ? parseInt(primarySelection.split(':')[1]) : null;
              const anchorGroup = anchorGroupId ? companyGroups.find(g => g.id === anchorGroupId) : null;
              const isTopLevelGroup = isGroupAnchor && anchorGroup?.parent_group == null;
              const divisions = isTopLevelGroup ? divisionsUnderGroup(anchorGroupId!) : [];

              // Build the list to render. For Group-with-divisions:
              // sub-headers per division. Otherwise a flat list.
              type Section = { headerLabel: string | null; sites: Company[] };
              let sections: Section[] = [];
              if (isTopLevelGroup && divisions.length > 0) {
                // Each division section. Sites whose group is the
                // top-level group directly (no division) get a
                // "Direct sites" bucket so they don't vanish.
                sections = divisions.map(div => ({
                  headerLabel: div.name,
                  sites: companies.filter(c => c.group === div.id),
                }));
                const directSites = companies.filter(c => c.group === anchorGroupId);
                if (directSites.length > 0) {
                  sections.unshift({ headerLabel: 'Direct sites', sites: directSites });
                }
              } else if (isGroupAnchor && anchorGroupId) {
                // Division anchor (no children) — flat list of every
                // site beneath, including primary.
                sections = [{ headerLabel: null, sites: sitesUnderGroup(anchorGroupId) }];
              } else {
                // Site anchor — legacy sister-sites behaviour
                // (primary already excluded by sisterSites memo).
                sections = [{ headerLabel: null, sites: sisterSites }];
              }

              const totalSites = sections.reduce((n, s) => n + s.sites.length, 0);
              if (totalSites === 0) {
                return (
                  <p className="text-sm text-muted-foreground italic">
                    {!isGroupAnchor && !primaryCompany?.group
                      ? `${primaryCompany?.name ?? 'This company'} isn't part of a company group, so there are no other sites to add.`
                      : `No sites available ${isGroupAnchor ? 'under this selection' : 'in this group'}.`}
                  </p>
                );
              }
              return (
                <div className="border rounded-lg divide-y">
                  {sections.map((section, idx) => (
                    <div key={`section-${idx}`}>
                      {section.headerLabel && (
                        <div className="px-3 py-1.5 bg-muted/40 text-xs uppercase tracking-wide text-muted-foreground font-medium flex items-center gap-2">
                          <Layers className="h-3 w-3" />{section.headerLabel}
                        </div>
                      )}
                      {section.sites.length === 0 ? (
                        <p className="px-3 py-2 text-xs italic text-muted-foreground">No sites under this division.</p>
                      ) : section.sites.map(site => {
                        const isPrimary = site.id === primaryCompanyId;
                        const checked = isPrimary || additionalSiteIds.has(site.id);
                        return (
                          <label key={site.id}
                            className={`flex items-center gap-3 px-3 py-2 ${isPrimary ? 'bg-blue-50/60 dark:bg-blue-950/20' : 'hover:bg-muted/40 cursor-pointer'}`}>
                            <Checkbox
                              checked={checked}
                              disabled={isPrimary}
                              onCheckedChange={(v: boolean | 'indeterminate') => {
                                if (isPrimary) return;
                                const next = new Set(additionalSiteIds);
                                if (v) next.add(site.id); else next.delete(site.id);
                                setAdditionalSiteIds(next);
                              }}
                            />
                            <span className="flex-1 text-sm">{site.name}</span>
                            {isPrimary && (
                              <Badge variant="secondary" className="text-[10px]">Primary</Badge>
                            )}
                            {site.country && (
                              <Badge variant="outline" className="text-[10px]">{site.country}</Badge>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  ))}
                </div>
              );
            })()}
            <p className="text-xs text-muted-foreground">
              Selected: {selectedSiteIds.length} site{selectedSiteIds.length === 1 ? '' : 's'} (including primary).
            </p>
          </div>
        )}

        <div className="space-y-2">
          {/* CR-7076: a role is a conscious, mandatory choice. The old
              "No specific role" default silently minted roleless users
              (no working permissions at all) — the dropdown now starts
              empty and Next is blocked until one is picked. */}
          <Label>User Role *</Label>
          <Select value={roleId?.toString() ?? ''}
            onValueChange={v => onRoleChange(parseInt(v))}>
            <SelectTrigger><SelectValue placeholder="Select a user role" /></SelectTrigger>
            <SelectContent>
              {roles.map(r => <SelectItem key={r.id} value={r.id.toString()}>{r.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </CardContent>
    </Card>
  );
}
